import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { analytics } from '../utils/analytics';

export const Footer: React.FC = () => {
  const handlePhoneClick = () => {
    analytics.trackPhoneCall();
  };

  const handleEmailClick = () => {
    analytics.trackEmailClick();
  };

  const handleSocialClick = (platform: string) => {
    analytics.trackSocialClick(platform);
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center" aria-label="OutstandingNet - Home">
              {/* Always use green logo in footer since it's on dark background */}
              <div className="h-16 w-auto">
                <img
                  src="/Logo-Green.png"
                  alt="OutstandingNet Logo"
                  className="w-full h-full object-contain"
                  width="160"
                  height="64"
                  loading="lazy"
                />
              </div>
            </Link>
            <p className="text-gray-300 leading-relaxed">
              We create outstanding web experiences that drive results. From design to deployment, 
              we're your trusted partner in digital success.
            </p>
            <div className="flex space-x-4">
              <a 
                href="#" 
                onClick={() => handleSocialClick('facebook')}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href="#" 
                onClick={() => handleSocialClick('twitter')}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href="#" 
                onClick={() => handleSocialClick('linkedin')}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href="#" 
                onClick={() => handleSocialClick('instagram')}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              <li><Link to="/" className="text-gray-300 hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors">About</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">Services</Link></li>
              <li><Link to="/portfolio" className="text-gray-300 hover:text-white transition-colors">Portfolio</Link></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Services</h3>
            <ul className="space-y-3">
              <li><Link to="/services#web-design" className="text-gray-300 hover:text-white transition-colors">Web Design</Link></li>
              <li><Link to="/services#web-development" className="text-gray-300 hover:text-white transition-colors">Web Development</Link></li>
              <li><Link to="/services#website-management" className="text-gray-300 hover:text-white transition-colors">Website Management</Link></li>
              <li><Link to="/services#seo-services" className="text-gray-300 hover:text-white transition-colors">SEO Services</Link></li>
              <li><Link to="/services#e-commerce" className="text-gray-300 hover:text-white transition-colors">E-commerce</Link></li>
              <li><Link to="/services#maintenance" className="text-gray-300 hover:text-white transition-colors">Maintenance</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact Info</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-primary-400" aria-hidden="true" />
                <a 
                  href="mailto:support@outstandingnet.com"
                  onClick={handleEmailClick}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  support@outstandingnet.com
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-primary-400" aria-hidden="true" />
                <a 
                  href="tel:5862484115"
                  onClick={handlePhoneClick}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  (586) 248-4115
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-primary-400" aria-hidden="true" />
                <span className="text-gray-300">11908 Cedarwood Dr, Shelby Township, MI 48315</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 OutstandingNet. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/cookie-policy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Cookie Policy
              </Link>
              <Link to="/service-level-agreement" className="text-gray-400 hover:text-white text-sm transition-colors">
                SLA
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};